import { useEffect, useState } from 'react';
import { labelGroupCaption } from '../labelVocab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Members } from './Members';
import { MarkdownEditor } from './MarkdownEditor';
import { QueryState } from './QueryState';
import { TaskStatusControl } from './TaskStatusControl';
import {
  getMe,
  listProjects,
  getProjectContents,
  createTask,
  createProject,
  updateProject,
  deleteProject,
  createNote,
  updateNote,
  deleteNote,
  ApiError,
  type Project,
  type ProjectType,
  type Note,
  getGoalTree,
  moveProject,
  claimTask,
  listLabels,
  listLabelGroups,
  listMembers,
  type Task,
} from '../api';
import { MultiSelect } from './MultiSelect';
import { ColorPicker } from './ColorPicker';
import { Select } from './Select';
import { useAssignees } from '../useAssignees';
import { useProjectRole } from '../roles';
import { useAccountDay } from '../accountDay';
import { readableDay, readableInstant, readableRange } from '../dateLabels';
import { FailureNote } from './FailureNote';
import { ProjectProgressLine } from './ProjectProgressLine';
import { projectOptions, renderProjectOption } from './pickerOptions';
import { goalOptions, renderGoalOption } from './pickerOptions';

const TYPE_LABEL: Record<ProjectType, string> = {
  trip: 'Trip',
  project: 'Project',
  event: 'Event',
};

/**
 * When a trip is, said the way a person would.
 *
 * This rendered `2026-09-12 → 2026-09-22`. The goals screen manages "24 days left" from exactly
 * the same information, and for a trip the countdown is the one number in the whole account that
 * would get somebody to open the app — so it was the one thing rendered as machine text.
 */
const dayCount = (iso: string, today: string) =>
  Math.round((Date.parse(`${iso}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000);

/*
  `today` is the account's day, handed in rather than read.

  This file carried a fourth copy of "what day is it", assembled from `getFullYear`/`getMonth`/
  `getDate` — the browser's — so "in 3 days" on a trip could be off by one for as many hours as the
  laptop and the profile disagree, and "today" could read "tomorrow" on the morning of departure.
*/
const dateRange = (project: Project, today: string) => {
  if (!project.startDate && !project.endDate) return 'no dates';
  if (!project.startDate) return `until ${readableDay(project.endDate!)}`;

  const days = dayCount(project.startDate, today);
  const when =
    days > 1
      ? `in ${days} days`
      : days === 1
        ? 'tomorrow'
        : days === 0
          ? 'today'
          : days === -1
            ? 'yesterday'
            : `${Math.abs(days)} days ago`;

  const span = project.endDate
    ? readableRange(project.startDate, project.endDate)
    : readableDay(project.startDate);
  // The countdown first, because that is the part you came for; the dates still there behind it.
  return `${when} · ${span}`;
};

/**
 * A trip crosses time zones, so its events are shown in the project's zone with the
 * viewer's own time alongside when they differ (§5.4). Showing only one of the two is
 * how you miss a flight.
 */
function EventTime({ iso, projectZone }: { iso: string; projectZone: string }) {
  const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fmt = (zone: string) => readableInstant(iso, zone);

  const there = fmt(projectZone);
  const here = fmt(viewerZone);

  return (
    <span>
      <strong>{there}</strong>
      {here !== there && <span className="muted"> ({here} your time)</span>}
    </span>
  );
}

function NoteEditor({
  note,
  projectId,
  onDone,
}: {
  note?: Note;
  projectId: string;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(note?.title ?? '');
  const [bodyMd, setBodyMd] = useState(note?.bodyMd ?? '');

  const save = useMutation({
    mutationFn: () =>
      note ? updateNote(note.id, { title, bodyMd }) : createNote({ projectId, title, bodyMd }),
    onSuccess: onDone,
  });

  return (
    <form
      className="panel"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim() || bodyMd.trim()) save.mutate();
      }}
    >
      <input value={title} placeholder="Note title" onChange={(e) => setTitle(e.target.value)} />
      <MarkdownEditor
        label="Note body"
        value={bodyMd}
        placeholder="Markdown — use the toolbar for a table"
        onChange={setBodyMd}
      />
      <div className="detail-foot">
        <button type="submit" className="primary" disabled={save.isPending}>
          Save
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Capture straight into a project.
 *
 * Mirrors the goal tree's inline "add the next step": from inside the container, the container is
 * already the answer, so nothing has to be picked. This is the affordance the project view never
 * had — you could read a project's contents and had no way to add to them.
 */
function ProjectCapture({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  /*
    Filing into a project is `write`, so this box works for a maintainer and an owner and nobody
    else. It was offered to everyone who could open the screen — including the reporter, for whom
    it was the second control on the same screen that answered a press with silence.
  */
  const { can, why } = useProjectRole(projectId);
  const mayFile = can('write');
  const whyNot = why('write');

  const add = useMutation({
    mutationFn: (submitted: string) => createTask({ title: submitted, projectId }),
    onSuccess: (_created, submitted) => {
      // Clear only if the field still holds what was submitted (I-78).
      setTitle((current) => (current === submitted ? '' : current));
      void queryClient.invalidateQueries();
    },
  });

  return (
    <>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) add.mutate(title.trim());
        }}
      >
        <input
          value={title}
          aria-label="Add a task to this project"
          placeholder="Add a task to this project"
          onChange={(e) => setTitle(e.target.value)}
          disabled={!mayFile}
          {...(whyNot ? { title: whyNot } : {})}
        />
        <button
          type="submit"
          disabled={!mayFile || !title.trim() || add.isPending}
          {...(whyNot ? { title: whyNot } : {})}
        >
          Add
        </button>
      </form>
      {/*
        Its own test id: the task panel carries a `role-note` too and both can be on the Projects
        screen at once, which makes an unqualified locator ambiguous rather than wrong — the worst
        kind of test failure to read.
      */}
      {whyNot && (
        <p className="subtext" data-testid="project-capture-role-note">
          {whyNot}
        </p>
      )}
      <FailureNote error={add.error} />
    </>
  );
}

/**
 * A project's work, unclaimed first.
 *
 * A project view is scanned for one thing: **what has nobody taken on.** Mixed in among assigned
 * work it answers the wrong question — you end up reading twenty rows to find the three that are
 * anybody's. So the pile comes first, then one group per person, and the ordering is not something
 * a sort control can undo, because "unclaimed first" is what the screen *is*.
 *
 * Before this it rendered `<li>{task.title}</li>`: unclickable strings with no status, no dates
 * and no way in. A container you cannot act on is a container with no reason to visit.
 */
function ProjectTasks({
  projectId,
  tasks,
  onOpenTask,
  onChanged,
}: {
  projectId: string;
  tasks: Task[];
  onOpenTask?: (id: string) => void;
  onChanged: () => void;
}) {
  const { myUserId, nameFor, canClaim } = useAssignees(tasks);
  const allLabels = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  const groups = useQuery({ queryKey: ['label-groups'], queryFn: listLabelGroups });
  // Only to name the project a shared group belongs to — see `labelGroupCaption`.
  const projectList = useQuery({ queryKey: ['projects'], queryFn: listProjects });
  const [labelId, setLabelId] = useState<string | null>(null);

  const claim = useMutation({ mutationFn: claimTask, onSuccess: onChanged });

  const mayClaim = canClaim(projectId);

  /*
    The project's own groups first, then yours, visibly separated (§6.4).

    The shared taxonomy is what a review is conducted through — "show me Phase 2 in this project" —
    and a personal label is filing nobody else can see. Printing them in one undifferentiated list
    would invite renaming a shared label to suit yourself, which changes everybody's screen.
  */
  const options = (groups.data ?? [])
    .slice()
    .sort((a, b) => Number(!!b.projectId) - Number(!!a.projectId))
    .flatMap((group) =>
      (allLabels.data ?? [])
        .filter((l) => l.groupId === group.id)
        .map((l) => ({
          value: l.id,
          label: l.name,
          data: { groupName: labelGroupCaption(group, projectList.data ?? []) },
        })),
    );

  const shown = labelId ? tasks.filter((t) => t.labelIds.includes(labelId)) : tasks;

  const unclaimed = shown.filter((t) => t.assigneeUserId === null);
  const holders = [
    ...new Set(shown.map((t) => t.assigneeUserId).filter((id): id is string => !!id)),
  ];
  // Yours first among the people, because "what have I got here" is the second question.
  holders.sort((a, b) => Number(b === myUserId) - Number(a === myUserId));

  const row = (task: Task) => (
    <li key={task.id} className={`task-row ${task.isBlocked ? 'is-blocked' : ''}`}>
      <TaskStatusControl task={task} />
      {onOpenTask ? (
        <button className="link task-title" onClick={() => onOpenTask(task.id)}>
          {task.title}
        </button>
      ) : (
        <span className={task.status === 'done' ? 'muted' : ''}>{task.title}</span>
      )}
      <span className="task-meta">
        {task.assigneeUserId === null && mayClaim && (
          /* One press, no dialog: the whole point of the pile is that clearing it is cheap. */
          <button className="link" onClick={() => claim.mutate(task.id)}>
            Take it on
          </button>
        )}
        {task.status !== 'done' && task.status !== 'next' && (
          <span className="pill">{task.status.replace('_', ' ')}</span>
        )}
        {task.subtasks.total > 0 && (
          <span className="pill">
            {task.subtasks.done}/{task.subtasks.total}
          </span>
        )}
        {task.scheduledDate && <span className="pill">{task.scheduledDate}</span>}
        {task.estimateMinutes && <span className="pill">{task.estimateMinutes}m</span>}
        {task.complexityPoints !== null && (
          <span className="pill">{task.complexityPoints} pts</span>
        )}
      </span>
    </li>
  );

  return (
    <div className="panel">
      <h3>Tasks ({shown.length})</h3>
      <ProjectCapture projectId={projectId} />

      {options.length > 0 && (
        <Select
          compact
          options={options}
          value={labelId}
          onChange={setLabelId}
          placeholder="Any label"
          emptyLabel="Any label"
          ariaLabel="Filter by label"
          renderOption={(o) => (
            <>
              {o.label}
              <span className="subtext"> {o.data?.groupName}</span>
            </>
          )}
        />
      )}

      {shown.length === 0 && <p className="muted">Nothing filed here yet.</p>}

      {unclaimed.length > 0 && (
        <>
          <h4 data-testid="unclaimed-heading">Nobody has taken these on ({unclaimed.length})</h4>
          <ul className="task-list" data-testid="unclaimed">
            {unclaimed.map(row)}
          </ul>
        </>
      )}

      {holders.map((userId) => (
        <div key={userId}>
          <h4>{userId === myUserId ? 'Yours' : (nameFor(userId) ?? 'Someone else')}</h4>
          <ul className="task-list" data-assignee={userId}>
            {shown.filter((t) => t.assigneeUserId === userId).map(row)}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ProjectView({
  projectId,
  onClose,
  onOpenTask,
  onOpenChild,
}: {
  projectId: string;
  onClose: () => void;
  onOpenTask?: (id: string) => void;
  /** Opening a child from the panel is the same act as opening one from the list. */
  onOpenChild: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [editingNote, setEditingNote] = useState<Note | 'new' | null>(null);
  const { today } = useAccountDay();

  // Who is looking, so the member panel knows whether to offer the owner's controls.
  // The server refuses regardless; this only decides what is worth showing.
  const me = useQuery({ queryKey: ['me'], queryFn: getMe }).data;
  const contents = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProjectContents(projectId),
  });
  /*
    The roster, under the same query key the People panel at the bottom of this panel already uses,
    so naming the four people at the top costs no extra request.

    `listMembers` rather than the member ids on `contents`: it is the one place a person becomes
    nameable to you — through membership, and only once they have accepted — and going anywhere
    else for a name is the slip behind three separate defects this month.
  */
  const roster = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => listMembers(projectId),
    staleTime: 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries();
  const archive = useMutation({
    mutationFn: () => updateProject(projectId, { archived: true }),
    onSuccess: () => {
      onClose();
      void invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteProject(projectId),
    onSuccess: () => {
      onClose();
      void invalidate();
    },
  });
  const removeNote = useMutation({ mutationFn: deleteNote, onSuccess: invalidate });
  const allProjects = useQuery({ queryKey: ['projects'], queryFn: listProjects });
  /*
    Everything except this project and its descendants.

    Removed rather than offered and refused: the service rejects a move inside your own subtree,
    but a picker that lists impossible answers is a picker that wastes a click to tell you so.
  */
  const parentOptions = projectOptions(
    (allProjects.data ?? []).filter(
      (candidate) => !candidate.path.startsWith(contents.data?.project.path ?? '\u0000'),
    ),
  ).map((option) => ({
    ...option,
    depth: (allProjects.data ?? []).find((x) => x.id === option.value)?.depth ?? 0,
  }));
  const move = useMutation({
    mutationFn: (parentId: string | null) => moveProject(projectId, parentId),
    onSuccess: invalidate,
  });
  const setColor = useMutation({
    mutationFn: (color: string | null) => updateProject(projectId, { color }),
    onSuccess: invalidate,
  });
  const setGoals = useMutation({
    mutationFn: (goalIds: string[]) => updateProject(projectId, { goalIds }),
    onSuccess: invalidate,
  });
  const goalTree = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const goalPickerOptions = goalOptions(goalTree.data ?? []);

  if (contents.isPending || contents.isError)
    return (
      <aside className="detail">
        <QueryState query={contents} className="muted" />
      </aside>
    );
  if (!contents.data) return null;

  const { project, tasks, events, notes, attachments } = contents.data;

  /*
    This project's direct children, ordered like the list behind the panel.

    Depth rather than a path prefix test: `path` is materialised, so a child is exactly one level
    deeper and sits under this project's path. Direct children only — the grandchildren belong on
    their own parent's panel, which is where somebody looking for them would go.
  */
  const children = [...(allProjects.data ?? [])]
    .filter((p) => p.parentProjectId === project.id)
    .sort((a, b) => a.path.localeCompare(b.path));

  /*
    Accepted only, which is the same set `progress.members` counts.

    A pending invitation has no name to print — `listMembers` withholds it, deliberately, so that
    inviting an id you guessed does not tell you whose it is — and a nameless row here would also
    make the roster one longer than the number beside it. Invitations have their own list in the
    People panel below, where acting on them belongs.
  */
  const accepted = (roster.data ?? []).filter((m) => m.acceptedAt !== null);

  return (
    <aside className="detail">
      <header className="detail-head">
        <h2>{project.title}</h2>
        {/* The same control the task panel uses. As a bare `link` it measured 13x31 — the exact
            defect the panel's own close button had, in a file that was out of the tree when that
            was fixed. */}
        <button className="icon-button detail-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <p className="muted">
        {TYPE_LABEL[project.type]} · {dateRange(project, today)} · {project.timezone}
      </p>
      {project.descriptionMd && <p>{project.descriptionMd}</p>}

      {/*
        What is happening, before what it is set to.

        This panel opened on Part of / Colour / Serves / Timeline — a settings form — on a project
        with eight tasks and four people on it. Everything needed was one layer down: the board
        already computes the status mix and `GET .../members` already returns all four with their
        roles. It just never reached the panel.
      */}
      <div className="panel project-summary" data-testid="project-summary">
        <h3>Where it stands</h3>
        <ProjectProgressLine progress={contents.data.progress} />
        {accepted.length > 0 && (
          <ul className="plain member-roster" data-testid="member-roster">
            {accepted.map((m) => (
              <li key={m.userId}>
                <span>{m.displayName || m.email}</span>
                <span className="pill">{m.role}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/*
        What this project is ultimately for.

        "Japan" the project and "visit japan" the goal both exist on the owner's account with
        nothing joining them, so every task inside the project had to be attached to the goal one
        at a time — which is a large part of why one task in twenty-one has a goal at all.

        Naming the goal here does *not* move the goal's progress bar. That percentage is still
        computed from the tasks attached to the goal and its sub-goals, exactly as before; making
        project work count is a separate change to numbers that have been read for weeks. The line
        below says so, rather than leaving it to be discovered.
      */}
      <div className="panel">
        <h3>Part of</h3>
        <Select
          options={parentOptions}
          value={project.parentProjectId}
          onChange={(next) => move.mutate(next)}
          placeholder="Nothing — a top-level project"
          emptyLabel="Nothing — a top-level project"
          ariaLabel={`Parent of ${project.title}`}
          renderOption={renderProjectOption}
        />
        {/*
          And what is part of *this*.

          The panel had a parent picker and no way to see the other direction, so a project's own
          screen could not tell you it had children — on an account whose deepest project sits
          three levels down. One line each, ordered by path like the list behind them, and each one
          opens.
        */}
        {children.length > 0 && (
          <ul className="plain project-children" data-testid="project-children">
            {children.map((child) => (
              <li key={child.id}>
                <button className="link" onClick={() => onOpenChild(child.id)}>
                  {child.title}
                </button>
                <span className="task-meta">
                  {/* Derived, like the list behind the panel — see the note on the row. */}
                  <span className="pill">{child.progress.status}</span>
                  <span className="pill">
                    {child.progress.done} of {child.progress.total} done
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="panel">
        <h3>Colour</h3>
        <p className="muted">
          Every task in this project takes it, unless the task has a colour of its own.
        </p>
        <ColorPicker
          name={`project-color-${project.id}`}
          label="Colour"
          value={project.color}
          onChange={(color) => setColor.mutate(color)}
        />
      </div>

      <div className="panel">
        <h3>Serves</h3>
        <MultiSelect
          options={goalPickerOptions}
          value={project.goalIds}
          onChange={(next) => setGoals.mutate(next)}
          placeholder="No goals"
          ariaLabel="Goals this project serves"
          dialogTitle="Goals this project serves"
          renderOption={renderGoalOption}
          renderChip={(option) => (
            <>
              <span className={`swatch ${option.data?.colorClass ?? ''}`} />
              {option.label}
            </>
          )}
        />
        <p className="subtext">
          A label, not a calculation — the goal’s progress still counts only the tasks attached to
          it directly.
          {/* A project's goal links are each member's own (01a157b3): the owner sees their goals
              here and a maintainer sees theirs, which would read as a bug if nothing said so. */}
          {accepted.length > 1 && ' Yours — each member files the project under their own goals.'}
        </p>
      </div>

      <div className="panel">
        <h3>Timeline</h3>
        {events.length === 0 && <p className="muted">Nothing scheduled yet.</p>}
        <ul>
          {events.map((e) => (
            <li key={e.id}>
              <EventTime iso={e.startsAt} projectZone={project.timezone} /> {e.title}
            </li>
          ))}
        </ul>
      </div>

      <ProjectTasks
        projectId={projectId}
        tasks={tasks}
        onOpenTask={onOpenTask}
        onChanged={invalidate}
      />

      <div className="panel">
        <h3>Notes</h3>
        {notes.length === 0 && !editingNote && <p className="muted">No notes yet.</p>}
        <ul>
          {notes.map((note) => (
            <li key={note.id}>
              <button className="link" onClick={() => setEditingNote(note)}>
                {note.title || '(untitled)'}
              </button>
              <button className="link danger" onClick={() => removeNote.mutate(note.id)}>
                ×
              </button>
            </li>
          ))}
        </ul>

        {editingNote ? (
          <NoteEditor
            {...(editingNote === 'new' ? {} : { note: editingNote })}
            projectId={projectId}
            onDone={() => {
              setEditingNote(null);
              void invalidate();
            }}
          />
        ) : (
          <button onClick={() => setEditingNote('new')}>Add a note</button>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="panel">
          <h3>Attachments</h3>
          <ul>
            {attachments.map((a) => (
              <li key={a.id}>{a.filename}</li>
            ))}
          </ul>
        </div>
      )}

      {/*
        Owner *or* maintainer: `admin` is the level the server asks for, and gating the controls on
        ownership meant a maintainer could share a project over the API and saw no way to here.
      */}
      <Members
        projectId={project.id}
        canAdminister={
          project.ownerUserId === me?.id ||
          contents.data.members.some((m) => m.userId === me?.id && m.role === 'maintainer')
        }
        myUserId={me?.id}
        onLeft={onClose}
      />

      <footer className="detail-foot">
        <button onClick={() => archive.mutate()}>Archive</button>
        <button
          className="danger"
          onClick={() => {
            if (confirm(`Delete "${project.title}"? Its tasks and events survive, detached.`))
              remove.mutate();
          }}
        >
          Delete
        </button>
      </footer>
    </aside>
  );
}

function NewProjectForm({ onDone }: { onDone: () => void }) {
  const [title, setTitle] = useState('');
  /*
    No default type.

    This initialised to `'trip'` with a title placeholder of "Japan", so the form was trip-shaped
    before you touched it — and three of the owner's four projects across two accounts are typed
    `trip`, including "Household Maintenance" and "Health", which plainly are not. The type is
    cosmetic today, but a container mis-labelled at birth stays mis-labelled.
  */
  const [type, setType] = useState<ProjectType | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timezone, setTimezone] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createProject({
        title: title.trim(),
        // Chosen, not inherited from a default. `project` is the neutral one when nothing is said.
        type: type === '' ? 'project' : type,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(timezone.trim() ? { timezone: timezone.trim() } : {}),
      }),
    onSuccess: onDone,
  });

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) create.mutate();
      }}
    >
      <h2>New project</h2>
      <div className="fields">
        <label>
          Title
          <input
            value={title}
            placeholder="What is this a container for?"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label>
          Type
          <select value={type} onChange={(e) => setType(e.target.value as ProjectType)}>
            <option value="">Choose…</option>
            <option value="trip">Trip</option>
            <option value="project">Project</option>
            <option value="event">Event</option>
          </select>
        </label>
        <label>
          Starts
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>
          Ends
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
        <label>
          Timezone
          <input
            value={timezone}
            placeholder={type === 'trip' ? 'Asia/Tokyo' : 'defaults to yours'}
            onChange={(e) => setTimezone(e.target.value)}
          />
        </label>
      </div>
      {create.error instanceof ApiError && <p className="banner">{create.error.message}</p>}
      <div className="detail-foot">
        <button type="submit" className="primary" disabled={!title.trim() || create.isPending}>
          Create
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function Projects({
  openProjectId,
  onOpenTask,
}: {
  openProjectId?: string | null;
  onOpenTask?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(openProjectId ?? null);
  const { today } = useAccountDay();

  /*
    A project hit in global search opens *that* project. Arriving on the list and having to find
    the thing you just searched for by name is the round trip the search box exists to remove.
  */
  useEffect(() => {
    if (openProjectId) setOpenId(openProjectId);
  }, [openProjectId]);

  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });

  return (
    /*
      An open project sits *beside* the list, not under it.

      The shell's two-column rule is keyed on `main.with-detail`, which this screen is not inside,
      so `workspace` alone laid out one column — and opening the fourth of seven projects pushed
      it below all seven cards. Same rule as everywhere else: opening a thing must not move the
      list you opened it from.

      `workspace` is on the section in both states, and only `has-detail` is conditional: the class
      carries a `margin-top`, so adding it on open slid the whole list down 24px as the panel
      appeared. The test measures that, because a 24px jump under the pointer is the kind of thing
      a screenshot flatters.
    */
    <section className={`workspace ${openId ? 'has-detail' : ''}`}>
      {/*
        `project-main`, not `main-pane`. This borrowed the app shell's layout class, which put two
        `.main-pane` elements in the DOM whenever the Projects screen was open — so any rule
        written for the shell silently applied inside this screen as well. The same collision
        shape as `.block` in Phase 7 and `.link.danger` in Phase 13.
      */}
      <div className="project-main">
        <div className="capture">
          {/*
            The disclosure disappears while the form is open rather than relabelling itself `Cancel` —
            see `Habits.tsx` for the argument. One form, one way out, and it sits beside the commit.
          */}
          {!adding && <button onClick={() => setAdding(true)}>New project</button>}
        </div>

        {adding && (
          <NewProjectForm
            onDone={() => {
              setAdding(false);
              void queryClient.invalidateQueries({ queryKey: ['projects'] });
            }}
          />
        )}

        <QueryState query={projects} />
        {projects.data?.length === 0 && !adding && (
          <p className="muted">
            No projects yet. A project is a container for related work — a trip, a piece of work, an
            event.
          </p>
        )}

        {/*
          The tree, on the screen whose whole job is showing projects.

          Seven flat cards, every one at the same x, with `SA: VLAN cutover` — a grandchild of
          `SA: Home lab rebuild 2026` — sitting last, six rows from its parent. The scope menu on
          Tasks already knows the whole hierarchy: it orders by the materialised path and prefixes
          a child with its parent, and the comment there names this exact symptom as the thing
          being fixed. The fix reached the menu and not this screen.

          Ordered by `path` and indented by `depth`, which is what the menu does — the same two
          fields, already on the payload. Indentation rather than nesting `<ul>`s, because a
          project's children are not a different kind of thing from it and the cards should stay
          one flat list to scan.
        */}
        <ul className="project-list">
          {[...(projects.data ?? [])]
            .sort((a, b) => a.path.localeCompare(b.path))
            .map((project) => (
              <li
                key={project.id}
                className="card"
                data-depth={project.depth}
                style={{ marginInlineStart: `${Math.min(project.depth, 4) * 1.25}rem` }}
              >
                <div className="template-head">
                  <button className="link template-name" onClick={() => setOpenId(project.id)}>
                    {project.title}
                  </button>
                  <span className="task-meta">
                    <span className="pill">{TYPE_LABEL[project.type]}</span>
                    {/*
                      `progress.status`, not `project.status`.

                      The stored column is hand-set, has no control anywhere in this app, and
                      nothing advances it — so every project ever created here reads `planning`
                      for life. The CSV-export project said `planning` with three of eight tasks
                      finished and one in progress, which is worse than saying nothing: a reviewer
                      scanning for what needs attention skips it. This one is computed from the
                      counts on the line below, so the badge and the count cannot disagree.
                    */}
                    <span className="pill">{project.progress.status}</span>
                    <span className="pill">{dateRange(project, today)}</span>
                    {project.timezone !== Intl.DateTimeFormat().resolvedOptions().timeZone && (
                      <span className="pill">{project.timezone}</span>
                    )}
                  </span>
                </div>
                <ProjectProgressLine progress={project.progress} />
              </li>
            ))}
        </ul>
      </div>

      {openId && (
        <ProjectView
          projectId={openId}
          onClose={() => setOpenId(null)}
          onOpenChild={setOpenId}
          {...(onOpenTask ? { onOpenTask } : {})}
        />
      )}
    </section>
  );
}
